import React, { useState } from "react";
import Select from "./../../../components/form/select/Select";
import RichTextEditor from "../../../components/form/RichTextEditor";
import { BsArrowLeft } from "react-icons/bs";
import useFileUpload from "../../../utils/hooks/useFileUpload";
import { FileUploader } from "react-drag-drop-files";
import { useEffect } from "react";
import ModalContainer from "../../../components/modals/ModalContainer";
import ViewFiles from "../../../components/modals/claims/ViewFiles";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  createClaim,
  getClaimProcessSettings,
  resetClaimStatus,
  setTempCoverInfo,
} from "../../../redux/slice/claim/claimSlice";
import { isEmptyObjectValues } from "./../../../utils/helpers/filter";
import { useNavigate } from "react-router-dom";
import { coverInfoSchema } from "../../../utils/validators/schema";
import { flashMessage as flash } from "../../../utils/flash";

export const joinBy_ = (parameter) => {
  return parameter
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, "_");
};

const flattenObject = (object) => {
  const objectData = {};
  if (object) {
    for (const element of object) {
      objectData[element] = "";
    }
    return objectData;
  }
};

export const formatDynamicValues = (values, fieldDefinitions) => {
  const dynamicFields = [];

  // Loop through the field definitions
  for (const definition of fieldDefinitions) {
    const field_name = joinBy_(definition.field_name.toLowerCase());
    const field_type = definition.field_type;
    const compulsory_field = definition.compulsory_field;

    // Check if the field_name exists in the values object
    if (values.hasOwnProperty(field_name)) {
      // Create a dynamic field object based on the values and definition
      const dynamicField = {
        field_name,
        field_type,
        compulsory_field,
        value: values[field_name],
      };

      // Push the dynamic field object to the dynamicFields array
      dynamicFields.push(dynamicField);
    }
  }
  return dynamicFields;
};

const CoverInfo = ({ policyHolderRef }) => {
  const {
    policyHolder,
    policy_cover,
    claim_process_settings,
    createSuccess,
    tempCoverInfo,
  } = useSelector((state) => state.claim);
  const { all_covers } = useSelector((state) => state.cover);

  const fileTypes = ["JPG", "PNG", "PDF", "DOC", "DOCX"];
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");
  const [detailsOfLoss, setDetailsOfLoss] = useState("");
  const [fileList, setFileList] = useState([]);
  const [policyCover, setPolicyCover] = useState("");
  const [disable, setDisable] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // upload file
  const { fileUrl, singleFileUpload } = useFileUpload();

  const today = new Date().toISOString().split("T")[0];

  const handleFileChange = (file) => {
    setDisable(true);
    setIsUploading(true);
    singleFileUpload(file);
    setSelectedFile("document-upload");
  };

  const handleCustomFileChange = (name, e) => {
    e.preventDefault();
    singleFileUpload(e.target.files[0]);
    setSelectedFile(name);
  };

  const onSubmit = (values) => {
    if (fileList.length < 1) {
      flash("error", "Document is required");
      return;
    }
    let payload = {
      policy_cover: values.policy_cover?.value,
      reason_for_claim: values.reason_for_claim,
      total_premium_paid: policyHolder?.total_premium_paid,
      claim_amount: values.claim_amount,
      premium_paid_date: policyHolder?.last_premium_paid_date,
      date_of_loss: values.date_of_loss,
      documents: fileList,
      policy_id: policyHolder?.app_details?.policy_id,
      office_id: policyHolder?.app_details?.cusomer_office_id,
      customer_id: policyHolder?.app_details?.customerId,
      policy_app_no: policyHolder?.app_details?.policyAppNumber,
      details_of_loss: detailsOfLoss,
      dynamic_fields: formatDynamicValues(
        values,
        claim_process_settings?.get_claim_form
      ),
    };
    dispatch(createClaim(payload));
  };

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    initialValues: tempCoverInfo
      ? tempCoverInfo
      : {
          policy_cover: policyCover,
          date_of_loss: "",
          premium_paid_date: "",
          claim_amount: "",
          total_premium_paid: "",
          reason_for_claim: "",
          document: [],
          ...flattenObject(
            claim_process_settings?.get_claim_form?.map((item) =>
              joinBy_(item?.field_name)
            )
          ),
        },
    validationSchema: coverInfoSchema,
    validate: (values) => {
      let errors = {};
      let customFormCompulsoryFields =
        claim_process_settings?.get_claim_form?.filter(
          (items) => items?.compulsory_field === 1
        );
      customFormCompulsoryFields?.forEach((field) => {
        const fieldName = joinBy_(field?.field_name);
        const value = values[fieldName];

        if (value === "") {
          errors[fieldName] = fieldName + " is required.";
        }
      });
      if (!values?.policy_cover?.value) {
        errors.policy_cover = "policy cover is required.";
      }

      return errors;
    },
    onSubmit,
  });

  useEffect(() => {
    if (isUploading) {
      setDisable(isUploading);
    } else if (isEmptyObjectValues(errors)) {
      setDisable(isEmptyObjectValues(errors));
    } else {
      setDisable(false);
    }
  }, [isUploading, values]);

  useEffect(() => {
    if (createSuccess) {
      resetClaimStatus();
      navigate("/admin/claims/all-claims");
      setFileList([]);
      resetClaimStatus();
    }
  }, [createSuccess]);

  useEffect(() => {
    if (fileUrl && selectedFile === "document-upload") {
      setIsUploading(false);
      let fileIndex = fileList?.findIndex((file) => file === fileUrl);
      if (fileIndex < 0) {
        setFileList([...fileList, fileUrl]);
      }
      return;
    }

    if (selectedFile !== "document-upload") {
      setFieldValue(selectedFile, fileUrl);
      setSelectedFile("file-upload");
    }
    // setFileList(tempCoverInfo?.document);
  }, [fileUrl, tempCoverInfo?.document]);

  useEffect(() => {
    if (values.policy_cover) {
      dispatch(
        getClaimProcessSettings({
          policy_cover: values?.policy_cover?.value,
          policy_id: policyHolder?.app_details?.policy_id,
        })
      );
      setPolicyCover(values.policy_cover);
      setFileList([]);
    }
  }, [values?.policy_cover]);

  return (
    <div className='row mx-2 sm-mx-0'>
      <div className='col-md-6'>
        <div>
          <label htmlFor='policy_cover' className='form-label'>
            Policy cover
          </label>
          <div>
            <Select
              data={all_covers?.unpaginated_cover
                ?.filter((cover) =>
                  policy_cover?.policy_cover?.some(
                    (element) => element.cover === String(cover.id)
                  )
                )
                .map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
              // data={policy_cover?.policy_cover?.map(({ cover }) => ({
              //   label: cover,
              //   value: cover,
              // }))}
              value={
                tempCoverInfo
                  ? tempCoverInfo.policy_cover?.value
                  : values?.policy_cover?.value
              }
              onChange={(value) => setFieldValue("policy_cover", value)}
              onBlur={() => {
                handleBlur({
                  target: { name: "policy_cover" },
                });
              }}
              name='policy_cover'
              isError={errors?.policy_cover && touched?.policy_cover}
            />
            {errors?.policy_cover && touched?.policy_cover ? (
              <span className='text-danger'>{errors.policy_cover}</span>
            ) : null}
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        <input
          autocomplete='false'
          name='hidden'
          type='text'
          style={{ display: "none" }}
        />
        <div className='col-md-12 my-3'>
          <div>
            <h1 className='fs-4 mb-1'>Claims Details</h1>
            <p>
              (field from the custom form associated with the selected policy
              cover will be displayed here)
            </p>
          </div>
          <div className='row'>
            <div className='col-sm-6 col-md-4 mb-3'>
              <label htmlFor='premium_paid_date' className='form-label'>
                Last premium paid Date <span className='text-danger'>*</span>
              </label>
              <input
                type='date'
                className={`form-control form-control-sm`}
                name='premium_paid_date'
                id='premium_paid_date'
                defaultValue={policyHolder?.last_premium_paid_date}
                disabled
                // onChange={handleChange}
                // onBlur={handleBlur}
              />
              {/* {errors.premium_paid_date && touched.premium_paid_date ? (
                <span className='fs-5 invalid-feedback'>
                  {errors.premium_paid_date}
                </span>
              ) : null} */}
            </div>
            <div className='col-sm-6 col-md-4 mb-3'>
              <label htmlFor='claim_amount' className='form-label'>
                Claim Amount <span className='text-danger'>*</span>
              </label>
              <input
                type='number'
                name='claim_amount'
                id='claim_amount'
                value={values.claim_amount}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`form-control form-control-sm ${
                  errors.claim_amount && touched.claim_amount && "is-invalid"
                }`}
              />
              {errors.claim_amount && touched.claim_amount ? (
                <span className='fs-5 invalid-feedback'>
                  {errors.claim_amount}
                </span>
              ) : null}
            </div>
            <div className='col-sm-6 col-md-4 mb-3'>
              <label htmlFor='total_premium_paid' className='form-label'>
                Total Premium paid <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                name='total_premium_paid'
                id='total_premium_paid'
                defaultValue={policyHolder?.total_premium_paid}
                className={`form-control form-control-sm`}
                disabled
              />
            </div>
            <div className='col-sm-6 col-md-4 mb-3'>
              <label htmlFor='reason_for_claim' className='form-label'>
                Reason for Claim <span className='text-danger'>*</span>
              </label>
              <input
                onChange={handleChange}
                onBlur={handleBlur}
                name='reason_for_claim'
                value={values.reason_for_claim}
                id='reason_for_claim'
                className={`form-control form-control-sm ${
                  errors.reason_for_claim &&
                  touched.reason_for_claim &&
                  "is-invalid"
                }`}
              />
              {errors.reason_for_claim && touched.reason_for_claim ? (
                <span className='fs-5 invalid-feedback'>
                  {errors.reason_for_claim}
                </span>
              ) : null}
            </div>
            <div className='col-sm-6 col-md-4 mb-3'>
              <label htmlFor='date_of_loss' className='form-label'>
                Date of Loss <span className='text-danger'>*</span>
              </label>
              <input
                type='date'
                name='date_of_loss'
                id='date_of_loss'
                value={values.date_of_loss}
                onChange={handleChange}
                onBlur={handleBlur}
                max={today}
                className={`form-control form-control-sm ${
                  errors.date_of_loss && touched.date_of_loss && "is-invalid"
                }`}
              />
              {errors.date_of_loss && touched.date_of_loss ? (
                <span className='fs-5 invalid-feedback'>
                  {errors.date_of_loss}
                </span>
              ) : null}
            </div>
            <div className='col-sm-6 col-md-4 mb-3'>
              <label htmlFor='date_of_loss' className='form-label'>
                Upload Document
                <span className='text-danger'>*</span>
                {isUploading && (
                  <span>
                    <i className='mdi mdi-dots-circle mdi-spin'></i>{" "}
                  </span>
                )}
                {fileList?.length > 0 &&
                  !isUploading &&
                  selectedFile === "document-upload" && (
                    <span>
                      <i className='mdi mdi-check'></i>
                    </span>
                  )}
              </label>
              <div>
                <FileUploader
                  label='Browse Document'
                  children={
                    <div
                      className={`border-dotted p-1 px-4 text-center cursor-p ${
                        errors.document && "is-invalid"
                      }`}
                      style={{ border: "2px dashed grey" }}
                      onClick={() => setSelectedFile("document-upload")}
                    >
                      <i className='uil uil-cloud-upload fs-4'></i> Upload
                      Document
                      <span className='text-primary-dark mt-0'></span>
                    </div>
                  }
                  handleChange={handleFileChange}
                  onBlur={handleBlur}
                  // onDrop={handleChange}
                  name='file'
                  types={fileTypes}
                />
              </div>
              {fileList?.length ? (
                <div className='mt-1 sm-mt-3'>
                  {fileList?.length} file{fileList?.length > 1 && "s"} uploaded{" "}
                  <span
                    className='text-info cursor-p'
                    data-bs-toggle='modal'
                    data-bs-target='#view_file'
                  >
                    view
                  </span>
                </div>
              ) : null}
            </div>
            <div className='mb-3' style={{ height: "250px" }}>
              <label htmlFor='details_of_loss' className='mb-1'>
                Details of Loss
              </label>
              <RichTextEditor
                name='details_of_loss'
                id='details_of_loss'
                value={detailsOfLoss}
                onChange={(value) => setDetailsOfLoss(value)}
                height='200px'
              />
            </div>
          </div>
        </div>
        <section>
          <div>
            <h1 className='fs-4 mb-1'>Custom Form</h1>
            <p>
              (field from the custom form associated with the selected policy
              cover will be displayed here)
            </p>
          </div>
          <div className='row'>
            {claim_process_settings?.get_claim_form &&
              claim_process_settings?.get_claim_form?.map(
                (customForm, index) => {
                  return (
                    <div className='mb-3 col-sm-9 col-md-7' key={index}>
                      {/* For inputs that are not checkbox */}
                      {customForm?.field_type !== "checkbox" &&
                      customForm?.field_type !== "file" &&
                      customForm?.field_type !== "button" ? (
                        <div>
                          <label htmlFor=''>
                            {customForm?.field_name.charAt(0).toUpperCase() +
                              customForm?.field_name?.slice(1)}
                          </label>
                          {customForm?.compulsory_field === 1 ? (
                            <span className='text-danger ms-1'>*</span>
                          ) : null}
                          <div>
                            <input
                              type={`${customForm?.field_type}`}
                              name={`${joinBy_(customForm?.field_name)}`}
                              className={`form-control form-control-sm`}
                              onChange={handleChange}
                              value={values[joinBy_(customForm?.field_name)]}
                              onBlur={handleBlur}
                              autoComplete='off'
                            />
                          </div>
                          {customForm?.compulsory_field === 1 &&
                          errors[joinBy_(customForm.field_name)] &&
                          touched[joinBy_(customForm.field_name)] ? (
                            <span className='text-danger'>
                              {customForm.field_name} is required
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                      {/* For checkbox input */}
                      {customForm?.field_type === "checkbox" && (
                        <div>
                          <div class='form-check'>
                            <input
                              class={`form-check-input ${
                                errors[joinBy_(customForm?.field_name)] &&
                                touched[joinBy_(customForm?.field_name)]
                              } `}
                              type='checkbox'
                              name={`${joinBy_(customForm?.field_name)}`}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label
                              className='form-check-label'
                              for='flexCheckDefault'
                              style={{ marginTop: "2px" }}
                            >
                              {customForm?.field_name.charAt(0).toUpperCase() +
                                customForm?.field_name?.slice(1)}
                            </label>
                            {customForm?.compulsory_field === 1 && (
                              <span className='text-danger ms-1'>*</span>
                            )}
                          </div>
                        </div>
                      )}
                      {/* For file inputs */}
                      {customForm?.field_type === "file" ? (
                        <div>
                          <label htmlFor=''>
                            {customForm?.field_name.charAt(0).toUpperCase() +
                              customForm?.field_name?.slice(1)}
                          </label>
                          {customForm?.compulsory_field === 1 && (
                            <span className='text-danger ms-1'>*</span>
                          )}
                          <div>
                            <input
                              type={`${customForm?.field_type}`}
                              name={`${joinBy_(customForm?.field_name)}`}
                              accept='.pdf, image/*'
                              className={`form-control form-control-sm`}
                              onChange={(e) =>
                                handleCustomFileChange(
                                  `${joinBy_(customForm?.field_name)}`,
                                  e
                                )
                              }
                              onBlur={handleBlur}
                            />
                            {customForm?.compulsory_field === 1 &&
                            errors[joinBy_(customForm.field_name)] &&
                            touched[joinBy_(customForm.field_name)] ? (
                              <span className='text-danger'>
                                {customForm.field_name} is required
                              </span>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                }
              )}
          </div>
        </section>
        <div className='d-flex justify-content-end gap-2 mt-2'>
          <button
            type='button'
            className='btn btn-sm btn-info '
            onClick={() => {
              dispatch(setTempCoverInfo({ ...values, document: fileList }));
              policyHolderRef.current?.click();
            }}
          >
            <BsArrowLeft className='me-1' size={18} />
            <span>Previous</span>
          </button>
          <button
            type='submit'
            disabled={disable}
            className='btn btn-sm btn-primary'
          >
            <span>Submit</span>
          </button>
        </div>
      </form>
      <ViewFiles modal_id='view_file' fileList={fileList} />
    </div>
  );
};

export default CoverInfo;
